import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { Accordion, AccordionItem } from '@react/react-spectrum/Accordion'
import Button from '@react/react-spectrum/Button'
import Dialog from '@react/react-spectrum/Dialog'
import FieldLabel from '@react/react-spectrum/FieldLabel'
import Heading from '@react/react-spectrum/Heading'
import ModalTrigger from '@react/react-spectrum/ModalTrigger'
import Select from '@react/react-spectrum/Select'

import Delete from '@react/react-spectrum/Icon/Delete'
import Edit from '@react/react-spectrum/Icon/Edit'

import IconPicker from 'components/IconPicker'
import MediaUpload from 'components/MediaUpload'
import ProductPicker from 'components/ProductPicker'
import { ScreenPicker } from '../partials/ScreenPicker'

import RenameScreenDialog from 'components/RenameScreen'
import Tooltip from 'components/Tooltip'

import { useProject, useScreens, useFeatures } from 'hooks'

import { PATHS } from '../../../config/constants'
import * as SIDEBARS from './sidebars'

import '../styles/ScreenSidebar.scss'

const TOOLTIP_STYLES = {
  position: 'absolute',
  top: '.5rem',
  right: '0',
  height: '12px',
  margin: 0,
  width: '12px',
  minWidth: '12px',
}

const isFreeformWebTemplate = (template) => template === 'FreeformPage'

export function ScreenSidebar({ screenName, screenTitle, setSelectedScreen }) {
  const { push } = useHistory()
  const { project, config, canEdit } = useProject()
  const { isOxygenAuthoringRedirectionEnabled } = useFeatures()
  const { screens, deleteScreen, findScreen, renameScreen, setScreenWithoutEditor } = useScreens()

  const [localScreen, setLocalScreen] = useState(null)
  const [shouldSave, setShouldSave] = useState(false)

  const localScreenTemplate = localScreen?.meta?.template
  const projectId = project?.meta?.id
  const projectType = project?.meta?.type ?? ''

  useEffect(() => {
    const s = findScreen(screenName)
    if ((s && !localScreen) || screenName !== localScreen?.meta?.name) {
      setLocalScreen(s)
    }
    setShouldSave(false)
  }, [screenName, screens, localScreen])

  const onMetaUpdate = useCallback((meta) => {
    setLocalScreen((s) => ({ ...s, meta: { ...s.meta, ...meta } }))
    setShouldSave(true)
  }, [])

  const onMetaPropertyUpdate = useCallback(
    (fieldName) => (value) => onMetaUpdate({ [fieldName]: value }),
    [onMetaUpdate]
  )

  const onContentUpdate = useCallback(
    (content) => {
      setLocalScreen((s) => ({ ...s, content }))
      setShouldSave(true)
    },
    [screenName]
  )

  useEffect(() => {
    if (shouldSave) setScreenWithoutEditor(localScreen?.meta?.name || screenName, localScreen)
  }, [localScreen, shouldSave])

  const onThumbnailUpdate = (thumbnail) => {
    const screen = { ...localScreen, meta: { ...localScreen.meta, thumbnail } }
    setScreenWithoutEditor(localScreen.meta?.name || screenName, screen)
    setLocalScreen(screen)
  }

  useEffect(() => {
    if (typeof screenTitle === 'string') {
      setLocalScreen((s) => {
        if (s) {
          return { ...s, meta: { ...s.meta, title: screenTitle } }
        }
      })
    }
  }, [screenTitle])

  const screenTemplates = useMemo(
    () =>
      Array.isArray(config.screenTemplates)
        ? config.screenTemplates.filter((t) => !t.hidden).map((t) => ({ ...t, value: t.template }))
        : [],
    [config]
  )

  const currentTemplateSettings = screenTemplates?.find((t) => t.value === localScreenTemplate)

  const templateSettings = useMemo(
    () => (Array.isArray(SIDEBARS[localScreenTemplate]) ? SIDEBARS[localScreenTemplate] : []),
    [localScreenTemplate]
  )

  const _renameScreen = ({ newTitle, newName }) => {
    renameScreen(localScreen?.meta?.name, newName, newTitle)
    setSelectedScreen({ name: newName, title: newTitle })
  }

  const openScreenEditor = useCallback(
    (viewMode) => {
      const pathPrefix = viewMode === true ? PATHS.WEB_MODULAR_PROJECT : PATHS.WEB_MODULAR_PROJECT_EDITOR
      const clientUrl = `${pathPrefix}${projectId}/${screenName}`

      if (viewMode === true) {
        window.open(clientUrl, '_blank')
        return
      }

      if (
        isFreeformWebTemplate(localScreenTemplate) &&
        isOxygenAuthoringRedirectionEnabled &&
        !localStorage.getItem('enableLegacyWebEditor')
      ) {
        window.open(clientUrl, '_blank')
        return
      }

      push(`${PATHS.PROJECT_DETAILS}${projectId}${PATHS.SCREENS_TAB}/${screenName}`)
    },
    [screenName, localScreenTemplate, projectId, push, isOxygenAuthoringRedirectionEnabled]
  )

  const isClassicTemplate = !!screenTemplates.find((t) => t.value === localScreenTemplate)

  return (
    <div className={'ScreenSidebar' + (screenName ? ' ScreenSidebar--isOpen' : '')}>
      {localScreen && (
        <>
          <div className='ScreenSidebar__tools'>
            <span className='ScreenSidebar__title'>
              <Heading size={2}>{screenTitle || screenName}</Heading>
              <Heading size={5}>{isClassicTemplate ? 'Fixed' : 'Freeform'} Template</Heading>
            </span>

            {canEdit && (
              <>
                <ModalTrigger>
                  <Button variant='tool' icon={<Edit />} />
                  <RenameScreenDialog screen={localScreen} screens={screens} onRename={_renameScreen} />
                </ModalTrigger>

                <ModalTrigger>
                  <Button variant='tool' className='delete-action' icon={<Delete />} />
                  <Dialog
                    variant='destructive'
                    title='Warning'
                    confirmLabel='OK'
                    cancelLabel='Cancel'
                    onConfirm={() => {
                      deleteScreen(screenName)
                      setSelectedScreen()
                    }}
                  >
                    Do you want to delete this screen?
                  </Dialog>
                </ModalTrigger>
              </>
            )}
          </div>

          <Accordion defaultSelectedIndex={0} multiselectable>
            <AccordionItem header='General'>
              {isClassicTemplate && (
                <FieldLabel label='Template' id='template-label' labelFor='localScreen-sidebar-template'>
                  <Select
                    aria-labelledby='template-label'
                    options={screenTemplates}
                    placeholder={'Screen template'}
                    value={localScreenTemplate}
                    id='localScreen-sidebar-template'
                    onChange={onMetaPropertyUpdate('template')}
                    disabled={!canEdit}
                  />
                </FieldLabel>
              )}

              <FieldLabel label='Thumbnail' labelFor='thumbnail'>
                <MediaUpload
                  type='image'
                  value={localScreen?.meta?.thumbnail}
                  maxHeight='100px'
                  onUpload={onThumbnailUpdate}
                  projectId={projectId}
                  path={`${screenName}-thumbnail`}
                  withTextfield={true}
                  disabled={!canEdit}
                />
              </FieldLabel>

              {project.meta.type === 'mobile' && (
                <FieldLabel label='Navigation Icon' labelFor='localScreen-sidebar-icon'>
                  <IconPicker
                    placeholder={'Icon'}
                    id='localScreen-sidebar-icon'
                    value={localScreen?.meta?.icon}
                    onChange={onMetaPropertyUpdate('icon')}
                    disabled={!canEdit}
                  />
                </FieldLabel>
              )}

              {project.meta.type === 'mobile-v2' && (
                <>
                  <FieldLabel label='Navigation Icon (active)' labelFor='localScreen-sidebar-icon'>
                    <IconPicker
                      placeholder={'Icon'}
                      id='localScreen-sidebar-icon'
                      value={localScreen?.meta?.icon}
                      onChange={onMetaPropertyUpdate('icon')}
                      disabled={!canEdit}
                      isMobileV2
                    />
                  </FieldLabel>
                  <FieldLabel label='Navigation Icon (inactive)' labelFor='localScreen-sidebar-icon'>
                    <IconPicker
                      placeholder={'Icon'}
                      id='localScreen-sidebar-icon'
                      value={localScreen?.meta?.iconInactive}
                      onChange={onMetaPropertyUpdate('iconInactive')}
                      disabled={!canEdit}
                      isMobileV2
                      isOutlineIcon
                    />
                  </FieldLabel>
                </>
              )}

              {currentTemplateSettings?.hasParent && (
                <FieldLabel label='Parent Screen'>
                  <ScreenPicker
                    screenName={screenName}
                    value={localScreen?.meta?.parentId}
                    onChange={onMetaPropertyUpdate('parentId')}
                    filter={(localScreen) => localScreen.meta.template === 'ProductPage'}
                    disabled={!canEdit}
                  />
                </FieldLabel>
              )}

              {!isClassicTemplate && (
                <div style={{ position: 'relative' }}>
                  <FieldLabel label='Product (forced)' labelFor='component-sidebar-product'>
                    <Tooltip
                      content='If set, page will ignore productId from URL. Selected product will be used in events and components. Do NOT set for a product template page.'
                      style={TOOLTIP_STYLES}
                    />

                    <ProductPicker
                      id='component-sidebar-product'
                      value={localScreen?.meta?.product || ''}
                      onChange={onMetaPropertyUpdate('product')}
                      disabled={!canEdit}
                    />
                  </FieldLabel>
                </div>
              )}
            </AccordionItem>

            {localScreen.content &&
              templateSettings.map((section) => {
                let Section = section.content

                const onChange = (fieldName) => (value) => {
                  onContentUpdate({ ...localScreen.content, [fieldName]: value })
                }

                return (
                  <AccordionItem header={section.label} key={section.label.replace(' ', '')}>
                    <Section
                      screen={localScreen}
                      project={project}
                      projectConfig={config}
                      onChange={onChange}
                      onContentUpdate={onContentUpdate}
                      onScreenUpdate={onMetaPropertyUpdate}
                      disabled={!canEdit}
                    />
                  </AccordionItem>
                )
              })}
          </Accordion>

          {!isClassicTemplate && (
            <div className='ScreenSidebar__actions'>
              <Button variant='cta' disabled={!canEdit} onClick={openScreenEditor}>
                Edit content
              </Button>
              {!projectType.startsWith('mobile') && (
                <Button variant='secondary' onClick={() => openScreenEditor(true)}>
                  View screen
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
